// Author : NANDHAKUMAR
// Version :0.0.1
// MODIFIED DATE: 14/11/2025
import moment, { Moment } from 'moment';

export const DATE_FORMAT = 'DD-MM-YYYY';
const API_DATE_FORMAT = 'YYYY-MM-DD';
export const MIN_FIXED_DEPOSIT_AMOUNT = 500;
export const MAX_FIXED_DEPOSIT_AMOUNT = 500000;
export const MIN_TENURE_MONTHS = 3;
export const MAX_TENURE_MONTHS = 120;
export const MIN_SHORT_TERM_DAYS = 7;
export const MAX_SHORT_TERM_DAYS = 14;

export type FixedDepositTenureType =
	| 'Monthly'
	| 'Quarterly'
	| 'HalfYearly'
	| 'OnMaturity'
	| 'ShortTerm';

export interface FixedDepositInput {
	amount: number;
	interestPercentage: number;
	tenureType: FixedDepositTenureType | '';
	tenureMonths?: number | null;
	tenureDays?: number | null;
	depositDate?: string | null;
}

export interface FixedDepositDateInfo {
	effectDate: string;
	maturityDate: string;
	nextInterestPayDate: string;
}

export interface FixedDepositInterestSummary {
	interestPayable: number;
	maturityAmount: number;
	nextInterestPayable: number;
}

const isValidAmount = (amount?: number | null): amount is number =>
	typeof amount === 'number' && !Number.isNaN(amount);

const toMoment = (date?: string | null): Moment | null => {
	if (!date) return null;
	const parsed = moment(date, DATE_FORMAT, true);
	return parsed.isValid() ? parsed : null;
};

const formatMoment = (date: Moment | null): string => (date ? date.format(DATE_FORMAT) : '');

export const validateDepositAmount = (amount?: number | null): string => {
	if (!isValidAmount(amount)) return '';
	if (amount < MIN_FIXED_DEPOSIT_AMOUNT || amount > MAX_FIXED_DEPOSIT_AMOUNT) {
		return `Amount is valid from Rs ${MIN_FIXED_DEPOSIT_AMOUNT.toLocaleString()} to Rs ${MAX_FIXED_DEPOSIT_AMOUNT.toLocaleString()}. Kindly fill accordingly`;
	}
	return '';
};

export const validateTenureMonths = (
	tenureMonths?: number | null,
	tenureType?: FixedDepositTenureType | '',
): string => {
	if (!isValidAmount(tenureMonths) || !tenureType || tenureType === 'ShortTerm') return '';

	if (tenureType === 'HalfYearly' && tenureMonths < 6) {
		return 'Please enter the value between 6 to 120';
	}

	if (tenureMonths < MIN_TENURE_MONTHS || tenureMonths > MAX_TENURE_MONTHS) {
		return `Please enter the value between ${MIN_TENURE_MONTHS} to ${MAX_TENURE_MONTHS}`;
	}

	return '';
};

export const validateTenureDays = (tenureDays?: number | null): string => {
	if (!isValidAmount(tenureDays)) return '';

	if (tenureDays < MIN_SHORT_TERM_DAYS || tenureDays > MAX_SHORT_TERM_DAYS) {
		return `Please enter the value between ${MIN_SHORT_TERM_DAYS} to ${MAX_SHORT_TERM_DAYS}`;
	}

	return '';
};

export const formatDateForApi = (date?: string | null): string => {
	if (!date) return '0';
	const parsed = moment(date, DATE_FORMAT, true);
	return parsed.isValid() ? parsed.format(API_DATE_FORMAT) : '0';
};

export const calculateFixedDepositDates = ({
	depositDate,
	tenureType,
	tenureMonths,
	tenureDays,
}: Pick<
	FixedDepositInput,
	'depositDate' | 'tenureType' | 'tenureMonths' | 'tenureDays'
>): FixedDepositDateInfo => {
	const effectDateMoment = toMoment(depositDate);
	if (!effectDateMoment || !tenureType) {
		return { effectDate: '', maturityDate: '', nextInterestPayDate: '' };
	}

	let maturityMoment: Moment | null = null;
	if (tenureType === 'ShortTerm') {
		if (!isValidAmount(tenureDays)) {
			return {
				effectDate: formatMoment(effectDateMoment),
				maturityDate: '',
				nextInterestPayDate: '',
			};
		}
		maturityMoment = effectDateMoment.clone().add(tenureDays!, 'days');
	} else {
		if (!isValidAmount(tenureMonths)) {
			return {
				effectDate: formatMoment(effectDateMoment),
				maturityDate: '',
				nextInterestPayDate: '',
			};
		}
		maturityMoment = effectDateMoment.clone().add(tenureMonths!, 'months');
	}

	let nextInterestMoment: Moment | null = null;
	switch (tenureType) {
		case 'Monthly':
			nextInterestMoment = effectDateMoment.clone().add(1, 'month');
			break;
		case 'Quarterly':
			nextInterestMoment = effectDateMoment.clone().add(3, 'months');
			break;
		case 'HalfYearly':
			nextInterestMoment = effectDateMoment.clone().add(6, 'months');
			break;
		case 'OnMaturity':
		case 'ShortTerm':
		default:
			nextInterestMoment = maturityMoment.clone();
			break;
	}

	return {
		effectDate: formatMoment(effectDateMoment),
		maturityDate: formatMoment(maturityMoment),
		nextInterestPayDate: formatMoment(nextInterestMoment),
	};
};

const getNoOfYears = ({
	tenureType,
	tenureMonths,
	tenureDays,
}: Pick<FixedDepositInput, 'tenureType' | 'tenureMonths' | 'tenureDays'>): number => {
	if (!tenureType) return 0;
	if (tenureType === 'ShortTerm') {
		return isValidAmount(tenureDays) ? tenureDays! / 365 : 0;
	}
	return isValidAmount(tenureMonths) ? tenureMonths! / 12 : 0;
};

const getNextPayoutYears = ({
	tenureType,
	noOfYears,
}: {
	tenureType: FixedDepositTenureType | '';
	noOfYears: number;
}): number => {
	switch (tenureType) {
		case 'Monthly':
			return 1 / 12;
		case 'Quarterly':
			return 3 / 12;
		case 'HalfYearly':
			return 6 / 12;
		case 'OnMaturity':
		case 'ShortTerm':
		default:
			return noOfYears;
	}
};

export const calculateInterestSummary = ({
	amount,
	interestPercentage,
	tenureType,
	tenureMonths,
	tenureDays,
}: FixedDepositInput): FixedDepositInterestSummary => {
	if (
		!isValidAmount(amount) ||
		!isValidAmount(interestPercentage) ||
		!tenureType ||
		interestPercentage <= 0 ||
		amount <= 0
	) {
		return { interestPayable: 0, maturityAmount: 0, nextInterestPayable: 0 };
	}

	const noOfYears = getNoOfYears({ tenureType, tenureMonths, tenureDays });
	if (noOfYears <= 0) {
		return { interestPayable: 0, maturityAmount: Number(amount), nextInterestPayable: 0 };
	}

	const principal = Number(amount);
	const rate = Number(interestPercentage) / 100;

	const interestPayable = Math.trunc(principal * noOfYears * rate);
	const maturityAmount = Math.trunc(principal) + interestPayable;

	const nextPayoutYears = getNextPayoutYears({ tenureType, noOfYears });
	const nextInterestPayable =
		nextPayoutYears > 0 ? Math.trunc(principal * nextPayoutYears * rate) : 0;

	return {
		interestPayable,
		maturityAmount,
		nextInterestPayable,
	};
};

export const deriveFixedDepositComputation = (input: FixedDepositInput) => {
	const amountError = validateDepositAmount(input.amount);
	const tenureMonthError = validateTenureMonths(input.tenureMonths, input.tenureType);
	const tenureDayError =
		input.tenureType === 'ShortTerm' ? validateTenureDays(input.tenureDays) : '';

	const dateInfo = calculateFixedDepositDates(input);
	const interestSummary = calculateInterestSummary(input);

	return {
		errors: {
			amountError,
			tenureMonthError,
			tenureDayError,
		},
		dateInfo,
		interestSummary,
	};
};
